<template>
    <div class="credit-card">
        <billing-pattern class="pattern" />

        <div class="card-header">
            <div class="logo">
                <CardLogo />
            </div>
            <div class="card-amount">
                <span>{{ formatCurrency(creditAmount) }}</span>
            </div>
        </div>

        <div class="card-body">
            <div class="card-info">
                <div class="card-name">{{ truncateText(email.toUpperCase(), 30) }}</div>
                <div class="card-number">{{ formatCardNumber(cardNumber) }}</div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import CardLogo from '@/assets/images/branding/card-logo.svg';
import BillingPattern from '@/assets/images/illustrations/billing-pattern.svg';
import { useSubscribeStore } from '@/stores/components/modals/subscription-modal';

const store = useSubscribeStore();
const { formatCurrency, truncateText } = useUtils();

// Define props with TypeScript types
interface Props {
    creditAmount: number;
    email: string;
    cardNumber: string;
}

const props = defineProps<Props>();

const formatCardNumber = (cardNumber: string): string => {
    // Remove any existing dashes and format as AAAA-AAAA-AAAA-AAAA
    const cleanNumber = cardNumber.replace(/-/g, '');
    return cleanNumber.replace(/(.{4})/g, '$1-').slice(0, -1);
};
</script>

<style scoped lang="scss">
.pattern {
    position: absolute;
    left: 0;
    bottom: 0;
}

.credit-card {
    @apply w-full lg:w-80 h-44 py-4 px-6 text-white font-inter relative overflow-hidden rounded-2xl border border-white;
    @apply flex justify-between flex-col;
    background: var(--gradient-gray-90070045-deg, linear-gradient(45deg, #181d27 0%, #414651 100%));
}

.card-header {
    @apply flex justify-between relative z-[1];
}

.logo {
    @apply flex items-center gap-2;
}

.card-body {
    @apply flex justify-between items-end relative z-[1];
}

.card-info {
    @apply flex flex-col gap-2;
}

.card-name {
    @apply text-white text-xs not-italic font-medium leading-normal uppercase tracking-[0.6px];
}

.card-number {
    @apply text-white text-base not-italic font-medium leading-normal tracking-[2.4px];
}

.card-amount {
    @apply rounded inline-flex py-2 px-2.5 justify-center items-center gap-2.5 text-zinc-50 text-right text-sm not-italic;
    @apply font-bold leading-normal tracking-[0.7px];
    border: 1px solid rgba(255, 255, 255, 0.25);
    background: rgba(9, 9, 11, 0.6);
}
</style>
